import { Button } from '../../ui/button/button';
import { Table } from '../../ui/table/table';
import { useDispatch } from 'react-redux';
import { useCallback, useState } from 'react';
import { dialogActions } from '../slice/dialog-slice';
import './lab-order.scss';
import { RoutingPath } from '../../../routes/routing-path';
import { push } from 'connected-react-router';
import { ColorIcon } from '../../ui/color-icon/color-icon';
import { ErrorOrder, orderPreparationActions } from '../../../slices/order-preparation-slice';
import { LoadingPopup } from '../../ui/loading/loading-popup';
import { LabOrderProps } from './lab-order';
import { printActions } from '../../../slices/sift/print-slice';
import { apiActions } from '../../../slices/api-slice';
import { ApiPrintShopOrderDataGet, PrintShopOrderDataGetResponse } from '../../../api/sift/front/api-print-meta-shop-order-data';
import { ResponseBase } from '../../../api/response-base';
import { PrintDataStructure } from '../../../models/sift/json/print-model';

export const LabOrderForPrint = (props: LabOrderProps) => {
  const { kijshopCd, orderList, callbackEndOrder, retry } = props;
  // - Hooks -
  const dispatch = useDispatch();
  // - state -
  const [orderingMessage, setOrderingMessage] = useState('');

  // -- 発注ボタン押下 --
  const handlerClickOrder = useCallback(async () => {
    const enableOrderList: { createDate: string, lastName: string, shopOrderId: string, status: string, cnt: number, cfOrderId?: string }[] = [];
    await Promise.all(orderList.map((v) => {
      return new Promise<void>((resolve) => {
        const shopOrderId = v.shopOrderId;
        dispatch(apiActions.run(
          new ApiPrintShopOrderDataGet({
            kijshopCd,
            shopOrderId,
          }),
          {
            onSuccess: (res: ResponseBase<PrintShopOrderDataGetResponse>) => {
              const target = res.body.data?.jsonList.find((v) => v.filename.includes(shopOrderId));
              if (!target) return resolve();
              const data = JSON.parse(target.body);
              const printData = new PrintDataStructure(`${target.filename}`, data);
              // 商品がない　または商品のうち一つでもラボ発注可じゃないものがあるかチェック
              const itemList = printData.getItemList();
              if (!itemList.length || itemList.some((item) => item.status !== 10)) {
                return resolve();
              }
              // 配送先情報が登録されているかチェック
              if (printData.deliveryData?.type === 'customer') {
                const deliveryData = printData.deliveryData;
                const customerDeliveryRequire = [
                  deliveryData.customerName,
                  deliveryData.address1,
                  deliveryData.prefecture,
                  deliveryData.zipCode,
                  deliveryData.phoneNumber,
                  deliveryData.timeZone,
                  deliveryData.formName,
                  deliveryData.pdfPath,
                ];
                if (customerDeliveryRequire.some((v) => !v)) {
                  return resolve();
                }
              }
              if (v.status === 'ラボ発注可' || retry) {
                enableOrderList.push({
                  createDate: v.createDate,
                  lastName: v.lastName,
                  shopOrderId: v.shopOrderId,
                  status: v.status,
                  cnt: v.cnt || 1,
                  cfOrderId: v.cfOrderId,
                });
              }
              resolve();
            },
            onError: () => {
              resolve();
            }
          }
        ));
      });
    }));
    if (!enableOrderList.length) {
      dispatch(dialogActions.pushMessage({
        title: '確認',
        message: ['発注できる注文がありません。'],
        buttons: [{
          label: 'OK',
          callback: () => {
            dispatch(dialogActions.pop());
          },
        }],
      }));
      return;
    }
    dispatch(dialogActions.pushMessage({
      title: '確認',
      message: [
        '発注処理を行います。よろしいですか？',
      ],
      buttons: [
        {
          label: 'いいえ',
          callback: () => {
            dispatch(dialogActions.pop());
          },
        },
        {
          label: 'はい',
          callback: async () => {
            dispatch(dialogActions.pop());
            const errorList: ErrorOrder[] = [];
            for (let i = 0; i < enableOrderList.length; i++) {
              setOrderingMessage(`ラボ発注を実施中です。（${i + 1}/${enableOrderList.length}）`);
              const orderData = enableOrderList[i];
              await new Promise<void>((resolve) => {
                dispatch(printActions.shopOrder({
                  kijshopCd,
                  shopOrderId: orderData.shopOrderId,
                }).labOrder({
                  isRetry: retry,
                  callback: {
                    success: () => {
                      resolve();
                    },
                    error: () => {
                      errorList.push({
                        ...orderData,
                        errorStatus: {
                          code: 'error',
                          message: ['ラボ発注処理に失敗しました'],
                        },
                        cnt: (orderData.cnt || 1) + 1,
                      });
                      resolve();
                    },
                    customError: ({ code, message }) => {
                      errorList.push({
                        ...orderData,
                        errorStatus: { code, message },
                        cnt: (orderData.cnt || 1) + 1,
                      });
                      resolve();
                    }
                  }
                }));
              });
            }
            dispatch(dialogActions.pop());
            setOrderingMessage('');
            if (errorList.length) {
              dispatch(orderPreparationActions.setErrorOrderList([...errorList]));
              dispatch(push(RoutingPath.cnv.uploadManagerForPrint({ kijshopCd })));
            } else {
              dispatch(dialogActions.pushMessage({
                title: '確認',
                message: [
                  '発注処理を行いました',
                  '',
                  '受注完了メールに納品日が記載されていますので',
                  '合わせてご確認ください',
                ],
                buttons: [{
                  label: 'OK',
                  callback: () => {
                    dispatch(dialogActions.pop());
                    callbackEndOrder();
                  },
                }],
              }));
            }
          },
        },
      ],
    }));
  }, [kijshopCd, orderList, retry]);

  // -- 閉じるボタン押下 --
  const handlerClickClose = useCallback(() => {
    dispatch(dialogActions.pop());
  }, []);

  const orderData = [...orderList].map((v) => [
    v.createDate || '',
    v.lastName || '',
    v.shopOrderId,
    <div className="order_status">
      <div className="order_status__inner">
        <ColorIcon color={v.status} />{v.status}
      </div>
    </div>,
    <div style={{ textAlign: 'center' }}>{v.status === 'ラボ発注可' ? '○' : '×'}</div>,
  ]);
  return (
    <div className='dialog_contents lab_order'>
      <div className='dialog_contents__body'>
        <Table
          head={['作成日時', 'お客様名', '注文番号', retry ? '状態' : 'ステータス', '発注可否']}
          body={orderData}
        />
      </div>
      <div className='dialog_contents__footer'>
        <Button
          label='キャンセル'
          onClick={handlerClickClose}
        />
        <Button
          label='発注'
          onClick={handlerClickOrder}
          color='warning'
        />
      </div>
      {orderingMessage ? (<LoadingPopup label={orderingMessage} />) : (<></>)}
    </div>
  );
};
